import { useMemo, useRef, useState } from "react";
import { AlertTriangle, FileText } from "lucide-react";

import {
    generationSettingStore,
    useAssignment,
    writeAssignmentInstruction,
} from "@entities/generation-setting";
import { putTaskReviewed, setReviewed, type TaskOutcome } from "@entities/generation-task";
import type { TaskReviewedMarks } from "@entities/generation-task";
import { navigateToTarget } from "@features/office";
import { usePaneManager } from "@features/pane-manager/store";
import type { useScrollMemory } from "@features/pane-manager/useScrollMemory";
import { Button } from "@shared/ui/Button";
import { Checkbox } from "@shared/ui/Checkbox";
import { notify } from "@shared/ui/toast/toastStore";

import {
    buildOutcomeRows,
    countOutcomeRows,
    keepRow,
    type OutcomeFilter,
    type OutcomeRow,
    type OutcomeTargetRow,
    type RunFilter,
} from "./buildOutcomeRows";
import {
    countReviewedTargets,
    isRowPartlyReviewed,
    isRowReviewed,
    isTargetReviewed,
    toggleRowKeys,
    toggleTargetKeys,
} from "./outcomeReviewed";

/**
 * 結果情報の一覧（アドイン）。
 *
 * **1 行は結果のタイトル 1 つ**で、行は担当と 1 対 1 になる。枠は行を開いたときの中身であって
 * 並びの単位ではない。グルーピングはスライドで行う
 * （[[2026-09-10 設計ノート アドイン 生成結果画面の結果情報対応]]）。
 *
 * Web コンソールと違うのは、**プロンプトの修正先が pptx の settings そのもの**である点である。
 * 上書きという間接の層を経ないので、直した瞬間に手元のファイルが変わる。
 */
export function OutcomeResults({
    outcome,
    reviewed: serverReviewed,
    taskId,
    projectId,
    scroll,
}: {
    outcome: TaskOutcome;
    reviewed: TaskReviewedMarks | null;
    taskId: string;
    projectId: string;
    scroll: ReturnType<typeof useScrollMemory>;
}) {
    const [filter, setFilter] = useState<OutcomeFilter>("all");
    const [runFilter, setRunFilter] = useState<RunFilter>("all");
    const [openKey, setOpenKey] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [localReviewed, setLocalReviewed] = useState<Set<string> | null>(null);
    /**
     * サーバーが受け取った最後の集合。保存が失敗したときの戻し先。押した直前の集合ではなく
     * これへ戻すのは、チェックが連打されうるためで、直前へ戻すとその間に通った別の保存ぶんまで
     * 巻き戻してしまう。
     */
    const lastSaved = useRef<Set<string> | null>(null);

    const groups = useMemo(() => buildOutcomeRows(outcome), [outcome]);
    const allRows = useMemo(() => groups.flatMap((g) => g.rows), [groups]);
    const marks = useMemo(() => {
        if (localReviewed) return localReviewed;
        return new Set(Object.keys(serverReviewed?.items ?? {}));
    }, [localReviewed, serverReviewed]);

    const visible = useMemo(
        () =>
            groups
                .map((g) => ({
                    ...g,
                    rows: g.rows.filter((r) =>
                        keepRow(r, filter, runFilter, skipOf(r.assignmentId))
                    ),
                }))
                .filter((g) => g.rows.length > 0),
        [groups, filter, runFilter]
    );

    const totals = countOutcomeRows(groups);
    const progress = countReviewedTargets(allRows, marks);

    /**
     * 確認済みを全置換で保存する。
     *
     * チェックは先に画面へ反映する（人手で確かめる画面で、押すたびに応答を待たせない）。ただし
     * 失敗したら**サーバーが受け取った最後の集合へ戻す**。戻さないと、通知が消えたあとも画面は
     * 「確認済み」を示し続け、記録が残っていないことに気付けない。
     */
    async function save(next: Set<string>): Promise<void> {
        setLocalReviewed(next);
        setBusy(true);
        try {
            const saved = await putTaskReviewed(projectId, taskId, Array.from(next));
            lastSaved.current = next;
            setLocalReviewed(next);
            // 取り直さずに手元の器へ映す。次に開いたときも同じものが出る。
            setReviewed(taskId, projectId, saved);
        } catch {
            notify("確認済みの記録を保存できませんでした", "error");
            setLocalReviewed(lastSaved.current);
        } finally {
            setBusy(false);
        }
    }

    return (
        <>
            <div className="shrink-0 space-y-2 px-4 pt-3 pb-2">
                <div className="flex flex-wrap items-center gap-1.5">
                    <FilterChip active={filter === "all"} onClick={() => setFilter("all")}>
                        全 {totals.rows}
                    </FilterChip>
                    <FilterChip active={filter === "alert"} onClick={() => setFilter("alert")}>
                        ▲依頼 {totals.alert}
                    </FilterChip>
                    <FilterChip active={filter === "grounds"} onClick={() => setFilter("grounds")}>
                        根拠 {totals.grounds}
                    </FilterChip>
                </div>
                {/* 次の再実行でどうなるかは、確かめる軸とは独立している。絞り込みに依らず常に出す。 */}
                <div className="flex flex-wrap items-center gap-1.5">
                    <FilterChip active={runFilter === "all"} onClick={() => setRunFilter("all")}>
                        すべて
                    </FilterChip>
                    <FilterChip active={runFilter === "skip"} onClick={() => setRunFilter("skip")}>
                        飛ばす
                    </FilterChip>
                    <FilterChip active={runFilter === "run"} onClick={() => setRunFilter("run")}>
                        実行する
                    </FilterChip>
                </div>
                <div className="flex items-center gap-2 text-[11px] text-text-muted">
                    <span className="shrink-0">確認済み</span>
                    <span className="h-1 flex-1 overflow-hidden rounded bg-border-subtle">
                        <span
                            className="block h-full bg-success transition-[width]"
                            style={{
                                width: progress.total
                                    ? `${Math.round((progress.done / progress.total) * 100)}%`
                                    : "0%",
                            }}
                        />
                    </span>
                    {/* 分母は枠の数。担当を分母にすると、枠が 30 ある担当と 1 つの担当が同じ重みになる。 */}
                    <span className="shrink-0 tabular-nums">
                        {progress.done} / {progress.total}
                    </span>
                </div>
            </div>

            <div ref={scroll.ref} className="flex-1 overflow-y-auto">
                {visible.length === 0 && (
                    <p className="px-4 py-6 text-center text-sm text-text-muted">
                        該当する項目がありません。
                    </p>
                )}
                {visible.map((group) => {
                    const done = group.rows.filter((r) => isRowReviewed(r, marks)).length;
                    return (
                        <div key={group.key}>
                            <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-border-subtle bg-sidebar-bg px-4 py-1 text-[11px] font-semibold text-text-muted">
                                <span className="flex-1 truncate">{group.title}</span>
                                {group.alertCount > 0 && (
                                    <span className="text-warning">▲{group.alertCount}</span>
                                )}
                                <span className="tabular-nums">
                                    {done}/{group.rows.length}
                                </span>
                            </div>
                            <div className="space-y-2 px-4 py-2">
                                {group.rows.map((row) => (
                                    <Row
                                        key={row.key}
                                        row={row}
                                        open={openKey === row.key}
                                        onToggleOpen={() =>
                                            setOpenKey(openKey === row.key ? null : row.key)
                                        }
                                        reviewed={marks}
                                        disabled={busy}
                                        onToggleRow={(checked) =>
                                            void save(toggleRowKeys(marks, [row], checked))
                                        }
                                        onToggleTarget={(target, checked) =>
                                            void save(toggleTargetKeys(marks, [target], checked))
                                        }
                                        scroll={scroll}
                                    />
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>
        </>
    );
}

/** 手元の settings が持つ「次回は飛ばす」の旗。**上書きではない。** */
function skipOf(assignmentId: string): boolean {
    return generationSettingStore.getAssignment(assignmentId)?.skip === true;
}

function FilterChip({
    active,
    onClick,
    children,
}: {
    active: boolean;
    onClick: () => void;
    children: React.ReactNode;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-pressed={active}
            className={`rounded-full border px-2 py-0.5 text-[11px] transition-colors ${
                active
                    ? "border-brand bg-brand/10 text-brand"
                    : "border-border-subtle text-text-muted hover:text-text-primary"
            }`}
        >
            {children}
        </button>
    );
}

function Row({
    row,
    open,
    onToggleOpen,
    reviewed,
    disabled,
    onToggleRow,
    onToggleTarget,
    scroll,
}: {
    row: OutcomeRow;
    open: boolean;
    onToggleOpen: () => void;
    reviewed: Set<string>;
    disabled: boolean;
    onToggleRow: (checked: boolean) => void;
    onToggleTarget: (target: OutcomeTargetRow, checked: boolean) => void;
    scroll: ReturnType<typeof useScrollMemory>;
}) {
    const done = isRowReviewed(row, reviewed);
    const partly = isRowPartlyReviewed(row, reviewed);
    return (
        <div
            data-anchor={row.assignmentId}
            className="rounded border border-border-subtle bg-surface"
        >
            <div className="flex items-center gap-2 p-2">
                {/* チェックと開閉は別要素にする。押し分けられないと確認のたびに行が開いてしまう。
                    行のチェックは配下の枠をまとめて動かすので 3 態を持つ。 */}
                <TriCheckbox
                    checked={done}
                    indeterminate={partly}
                    disabled={disabled}
                    onChange={onToggleRow}
                    ariaLabel={`${row.headline} をまとめて確認済みにする`}
                />
                <StateDot state={row.state.code} label={row.state.label} />
                <button
                    type="button"
                    onClick={onToggleOpen}
                    className="min-w-0 flex-1 truncate text-left text-[13px] font-medium text-text-primary"
                >
                    {row.headline}
                </button>
            </div>
            {row.marks.length > 0 && (
                <div className="flex flex-wrap items-center gap-1 px-2 pb-1.5 pl-9 text-[11px]">
                    {row.marks.map((mark) => (
                        <span
                            key={mark.label}
                            className={mark.alert ? "text-warning" : "text-text-muted"}
                        >
                            {mark.label}
                        </span>
                    ))}
                </div>
            )}

            {open && (
                <div className="space-y-2 border-t border-border-subtle px-2 py-2 text-[12px]">
                    {row.report && (
                        <p className="whitespace-pre-wrap text-text-secondary">{row.report}</p>
                    )}
                    {row.request && (
                        <p className="flex items-start gap-1.5 rounded bg-warning/10 p-1.5 text-warning">
                            <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                            <span>{row.request}</span>
                        </p>
                    )}
                    {row.sources.length > 0 && (
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-text-muted">
                            <span className="shrink-0">読んだ資料</span>
                            {row.sources.map((s) => (
                                <span
                                    key={s.sourceId}
                                    // 使わなかった資料も出す。確かめた範囲が見えることに意味がある。
                                    title={s.grounded ? "裏付けに使った" : "読んだが使わなかった"}
                                    className={
                                        s.grounded
                                            ? "text-interactive"
                                            : "text-text-muted line-through decoration-text-muted/40"
                                    }
                                >
                                    {s.fileName || s.sourceId}
                                </span>
                            ))}
                        </div>
                    )}
                    {row.targets.map((target) => (
                        <TargetLine
                            key={target.targetRef}
                            target={target}
                            checked={isTargetReviewed(target, reviewed)}
                            disabled={disabled}
                            onCheck={(next) => onToggleTarget(target, next)}
                        />
                    ))}
                    <InstructionEditor assignmentId={row.assignmentId} scroll={scroll} />
                </div>
            )}
        </div>
    );
}

/** 状態の丸。4 値あるので色で示す。読み上げには語を渡す。 */
function StateDot({ state, label }: { state: string; label: string }) {
    const color =
        state === "success"
            ? "bg-success"
            : state === "warning"
              ? "bg-warning"
              : state === "error"
                ? "bg-danger"
                : "border border-dashed border-text-muted";
    return (
        <span
            aria-label={label}
            title={label}
            className={`size-2 shrink-0 rounded-full ${color}`}
        />
    );
}

/** 全件・一部・なしの 3 態。共有の Checkbox は 3 態を持たないためここだけ素の input を使う。 */
function TriCheckbox({
    checked,
    indeterminate,
    disabled,
    onChange,
    ariaLabel,
}: {
    checked: boolean;
    indeterminate: boolean;
    disabled: boolean;
    onChange: (checked: boolean) => void;
    ariaLabel: string;
}) {
    return (
        <input
            type="checkbox"
            checked={checked}
            disabled={disabled}
            // indeterminate は属性で表せないので ref のコールバックで書く。3 態の遷移だけに要る。
            ref={(el) => {
                if (el) el.indeterminate = indeterminate;
            }}
            onChange={(e) => onChange(e.target.checked)}
            aria-label={ariaLabel}
            className="size-3.5 shrink-0 cursor-pointer accent-brand"
        />
    );
}

/**
 * 枠 1 つの欄。**行ではないので、行と同じ重さで出さない。**
 *
 * 確認のチェックはここにも置く。行のチェックだけにすると、担当の中の 1 つだけ確かめたい場合に
 * 「まだ見ていない枠を見たことにする」ことになる。
 */
function TargetLine({
    target,
    checked,
    disabled,
    onCheck,
}: {
    target: OutcomeTargetRow;
    checked: boolean;
    disabled: boolean;
    onCheck: (checked: boolean) => void;
}) {
    const [open, setOpen] = useState(false);
    function handleNavigate(): void {
        void navigateToTarget(target.targetRef).then((r) => {
            if (r === "not-found") notify("対象の要素が見つかりません", "error");
        });
    }
    return (
        <div className="rounded border border-border-subtle">
            <div className="flex items-center gap-1.5 px-1.5 py-1">
                <Checkbox
                    checked={checked}
                    disabled={disabled}
                    onChange={onCheck}
                    ariaLabel={`${target.name} を確認済みにする`}
                />
                <button
                    type="button"
                    onClick={handleNavigate}
                    title="PowerPoint で表示"
                    className="shrink-0 rounded bg-sidebar-bg px-1 py-0.5 text-[10px] font-semibold text-text-muted transition-colors hover:bg-brand/10 hover:text-brand"
                >
                    {target.targetRef.startsWith("slide:") ? "スライド" : "図形"}
                </button>
                <button
                    type="button"
                    onClick={() => setOpen(!open)}
                    className="min-w-0 flex-1 truncate text-left text-[11px] text-text-secondary"
                >
                    {target.name}
                </button>
                <span className="shrink-0 text-[10px] text-text-muted">{target.write.label}</span>
            </div>
            {open && (
                <div className="space-y-0.5 border-t border-border-subtle px-1.5 py-1 text-[11px] text-text-muted">
                    {target.grounds.length > 0 ? (
                        target.grounds.map((g, i) => (
                            <p key={i} className="flex items-center gap-1">
                                <FileText size={10} className="shrink-0" />
                                <span className="truncate">{g.fileName || g.sourceId}</span>
                                {g.locator && <span className="font-mono">{g.locator}</span>}
                            </p>
                        ))
                    ) : (
                        <p>根拠は申告されていません。</p>
                    )}
                    {/* 仮の名前は作り手が付けた名前ではない。**そう言わないと探しに行かれる。** */}
                    {!target.named && <p>この枠には名前がありません。画面 C で付けられます。</p>}
                </div>
            )}
        </div>
    );
}

/**
 * 本文の編集欄。
 *
 * **担当のペインと同じ settings を購読する。** 写しを持たないので、どちらで直しても同じ瞬間に
 * もう片方へ映る。保存先は pptx の settings そのもので、上書きレイヤーではない。
 *
 * **作法は `writeAssignmentInstruction` が持つ。** 本文を変えると対象が作り直され、書き込まない
 * 旗も同じ瞬間に絞られる。ここで組み立てると担当のペインと片方だけずれる。
 *
 * 対象・書き込まない旗・削除は担当のペインが持つ。幅に収まらず、結果を見ながら触るものでもない。
 */
function InstructionEditor({
    assignmentId,
    scroll,
}: {
    assignmentId: string;
    scroll: ReturnType<typeof useScrollMemory>;
}) {
    const openPane = usePaneManager((s) => s.openPane);
    const assignment = useAssignment(assignmentId);
    if (!assignment) {
        return (
            <p className="text-[11px] text-text-muted">
                この担当は手元の設定にありません。編集用の PPTX を開いているか確認してください。
            </p>
        );
    }
    return (
        <div className="space-y-1.5 rounded bg-sidebar-bg p-2">
            <div className="flex items-center justify-between gap-2">
                <span className="truncate text-[11px] font-semibold text-text-muted">
                    {assignment.label || "無題"}
                </span>
                <Button
                    variant="secondary"
                    onClick={() => {
                        // 戻ってきたら出ていった行へ帰す。
                        scroll.leaveFrom(assignmentId);
                        openPane("assignment", assignmentId);
                    }}
                >
                    対象と設定
                </Button>
            </div>
            <textarea
                value={assignment.instruction}
                onChange={(e) =>
                    writeAssignmentInstruction(assignmentId, e.target.value, assignment)
                }
                rows={4}
                className="w-full resize-y rounded border border-border-subtle bg-surface p-1.5 text-[12px] text-text-primary"
                placeholder="このスライドに何を書くかを指示します"
            />
        </div>
    );
}
